import React, { useState, useEffect } from 'react';
import {
  Card, Row, Col, Statistic, Table, Select, DatePicker, Space, Tag, Typography,
  message, Spin, Progress, Tooltip,
} from 'antd';
import {
  TrophyOutlined, BarChartOutlined, ClockCircleOutlined, GoldOutlined,
} from '@ant-design/icons';
import { getSmelterPerformance, getMonthlyTrend, fetchSmelters } from '../api';
import { usePermissions } from '../../../hooks/usePermissions';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const fmt = (v, decimals = 2) => (v ?? 0).toFixed(decimals);
const fmtRupee = (v) => `₹${Number(v ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

export default function SmelterPerformance() {
  const { can } = usePermissions();
  // Recovery and settlement analytics stay visible to anyone who can open the
  // page; the margin columns need `inventoryProfit:view`.
  const canViewProfit = can('inventoryProfit', 'view');
  const [smelters, setSmelters] = useState([]);
  const [performance, setPerformance] = useState({ smelters: [], overall: {} });
  const [monthlyTrend, setMonthlyTrend] = useState([]);
  const [filters, setFilters] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchSmelters().then((r) => setSmelters(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [perfResp, trendResp] = await Promise.all([
          getSmelterPerformance(filters),
          getMonthlyTrend(filters.smelterId ? { smelterId: filters.smelterId } : {}),
        ]);
        setPerformance(perfResp.data || { smelters: [], overall: {} });
        setMonthlyTrend(trendResp.data || []);
      } catch {
        message.error('Failed to load performance data');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [filters]);

  const { overall } = performance;

  const kpiCards = [
    canViewProfit && {
      title: 'Total Profit',
      value: fmtRupee(overall.totalProfit),
      icon: <TrophyOutlined style={{ color: '#52c41a', fontSize: 22 }} />,
      suffix: '',
    },
    canViewProfit && {
      title: 'Avg Profit %',
      value: `${fmt(overall.avgProfitPct)}%`,
      icon: <BarChartOutlined style={{ color: '#1677ff', fontSize: 22 }} />,
    },
    {
      title: 'Avg Settlement',
      value: `${fmt(overall.avgSettlementDays, 1)} days`,
      icon: <ClockCircleOutlined style={{ color: '#fa8c16', fontSize: 22 }} />,
    },
    {
      title: 'Avg Weight Recovery',
      value: `${fmt(overall.avgWeightRecoveryPct)}%`,
      icon: <GoldOutlined style={{ color: '#faad14', fontSize: 22 }} />,
    },
  ].filter(Boolean);

  const smelterColumns = [
    {
      title: 'Smelter',
      dataIndex: 'smelterName',
      render: (name) => <Text strong>{name || 'Unknown'}</Text>,
    },
    {
      title: 'Dispatches',
      dataIndex: 'totalDispatches',
      sorter: (a, b) => a.totalDispatches - b.totalDispatches,
    },
    {
      title: 'Total Gross Dispatched',
      dataIndex: 'totalGrossDispatched',
      render: (v) => `${fmt(v)}g`,
      sorter: (a, b) => a.totalGrossDispatched - b.totalGrossDispatched,
    },
    {
      title: 'Total Refined',
      dataIndex: 'totalRefinedWeight',
      render: (v) => `${fmt(v)}g`,
    },
    {
      title: 'Weight Recovery',
      dataIndex: 'overallWeightRecoveryPct',
      render: (v) => (
        <Tooltip title={`${fmt(v)}%`}>
          <Progress
            percent={Math.round(v || 0)}
            size="small"
            strokeColor={v >= 95 ? '#52c41a' : v >= 90 ? '#faad14' : '#ff4d4f'}
            style={{ width: 80 }}
          />
        </Tooltip>
      ),
      sorter: (a, b) => (a.overallWeightRecoveryPct || 0) - (b.overallWeightRecoveryPct || 0),
    },
    {
      title: 'Total Revenue',
      dataIndex: 'totalRevenue',
      render: (v) => fmtRupee(v),
      sorter: (a, b) => a.totalRevenue - b.totalRevenue,
    },
    canViewProfit && {
      title: 'Total Profit',
      dataIndex: 'totalProfit',
      render: (v) => (
        <Text style={{ color: v >= 0 ? '#52c41a' : '#ff4d4f' }}>{fmtRupee(v)}</Text>
      ),
      sorter: (a, b) => a.totalProfit - b.totalProfit,
      defaultSortOrder: 'descend',
    },
    canViewProfit && {
      title: 'Avg Profit %',
      dataIndex: 'avgProfitPct',
      render: (v) => (
        <Tag color={v >= 10 ? 'green' : v >= 5 ? 'orange' : 'red'}>
          {fmt(v)}%
        </Tag>
      ),
      sorter: (a, b) => (a.avgProfitPct || 0) - (b.avgProfitPct || 0),
    },
    {
      title: 'Avg Settlement',
      dataIndex: 'avgSettlementDays',
      render: (v) => v != null ? `${fmt(v, 1)} days` : '—',
      sorter: (a, b) => (a.avgSettlementDays || 0) - (b.avgSettlementDays || 0),
    },
    {
      title: 'Last Dispatch',
      dataIndex: 'lastDispatchDate',
      render: (v) => v ? new Date(v).toLocaleDateString('en-IN') : '—',
    },
  ].filter(Boolean);

  const trendColumns = [
    { title: 'Month', dataIndex: 'monthLabel', render: (v) => <Text strong>{v}</Text> },
    { title: 'Dispatches', dataIndex: 'dispatches' },
    { title: 'Gross Dispatched', dataIndex: 'totalGrossDispatched', render: (v) => `${fmt(v)}g` },
    { title: 'Refined Weight', dataIndex: 'totalRefinedWeight', render: (v) => `${fmt(v)}g` },
    { title: 'Revenue', dataIndex: 'totalRevenue', render: fmtRupee },
    canViewProfit && {
      title: 'Profit',
      dataIndex: 'totalProfit',
      render: (v) => <Text style={{ color: v >= 0 ? '#52c41a' : '#ff4d4f' }}>{fmtRupee(v)}</Text>,
    },
    canViewProfit && {
      title: 'Avg Profit %',
      dataIndex: 'avgProfitPct',
      render: (v) => <Tag color={v >= 10 ? 'green' : v >= 5 ? 'orange' : 'red'}>{fmt(v)}%</Tag>,
    },
  ].filter(Boolean);

  return (
    <div style={{ padding: 24 }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 20 }}>
        <Col>
          <Title level={4} style={{ margin: 0 }}>Smelter Performance Dashboard</Title>
          <Text type="secondary">Recovery rates, profit margins, and settlement analytics</Text>
        </Col>
        <Col>
          <Space>
            <Select
              placeholder="Filter by Smelter"
              allowClear
              style={{ width: 200 }}
              onChange={(v) => setFilters((f) => ({ ...f, smelterId: v }))}
              options={smelters.map((s) => ({ value: s._id, label: s.name }))}
            />
            <RangePicker
              onChange={(dates) => setFilters((f) => ({
                ...f,
                from: dates?.[0]?.toISOString(),
                to: dates?.[1]?.toISOString(),
              }))}
            />
          </Space>
        </Col>
      </Row>

      <Spin spinning={loading}>
        {/* KPI Cards */}
        <Row gutter={16} style={{ marginBottom: 20 }}>
          {kpiCards.map((kpi) => (
            <Col xs={24} sm={12} lg={6} key={kpi.title}>
              <Card>
                <Row align="middle" justify="space-between">
                  <Col>
                    <Text type="secondary" style={{ fontSize: 12 }}>{kpi.title}</Text>
                    <div>
                      <Text strong style={{ fontSize: 22 }}>{kpi.value}</Text>
                    </div>
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      {overall.totalDispatches || 0} completed dispatches
                    </Text>
                  </Col>
                  <Col>{kpi.icon}</Col>
                </Row>
              </Card>
            </Col>
          ))}
        </Row>

        {/* Per-smelter table */}
        <Card title="Per-Smelter Performance" style={{ marginBottom: 20 }}>
          <Table
            dataSource={performance.smelters}
            columns={smelterColumns}
            rowKey="_id"
            size="small"
            pagination={false}
            locale={{ emptyText: 'No settled dispatches in selected range' }}
            scroll={{ x: 1200 }}
          />
        </Card>

        {/* Monthly trend */}
        <Card title="Monthly Trend (Last 12 Months)">
          <Table
            dataSource={monthlyTrend}
            columns={trendColumns}
            rowKey="monthLabel"
            size="small"
            pagination={false}
            locale={{ emptyText: 'No data' }}
          />
        </Card>
      </Spin>
    </div>
  );
}
